import { HttpClient, HttpHeaders, HttpRequest, HttpResponse,
       } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import 'rxjs/operators/map';

import { ProductDesignerComponentConfig
       } from 'app/product-designer-component-config';
import { ProductConfig } from 'app/product-designer-config';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { TrivialError } from 'core/error/error';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';

interface StructureInfo
{
  structure: string;
  template_group: string;
  template: string;
}

export class BuildPostDataError extends TrivialError
{
  protected _name: string = 'BuildPostDataError';
}

@Injectable()
export class ProductdesignerdService
{
  private _heartbeatURL: undefined|string;
  private _designResultsURL: undefined|string;

  constructor(
      private http: HttpClient,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(LoggingService) private loggingService: LoggingService,
  )
  { }

  public async postProducts(
      productDesignerComponentConfig: ProductDesignerComponentConfig,
      productConfig: ProductConfig): Promise<string>
  {
    return new Promise<string>(async (resolve, reject) => {
      try {
        let postData = this.buildPostData(
            productDesignerComponentConfig.cartURL,
            productDesignerComponentConfig.productStoreURL,
            productDesignerComponentConfig.catalogStoreId,
            productDesignerComponentConfig.locale,
            productConfig);

        const req = new HttpRequest('POST', this._designResultsURL,
            postData, { headers: new HttpHeaders() });

        const resp = await this.http.request(req)
          .pipe(Utils.retryPipe())
          .toPromise()
          .catch((error) => {
            throw error;
          });

        if (resp instanceof HttpResponse) {
          if (resp.status >= 200 && resp.status < 300) {
            const location = resp.body['redirect_target_url'];
            if (location === undefined) {
              reject(location);
            }
            else {
              resolve(location);
            }
          }
          else {
            reject(resp.status);
          }
        }
      }
      catch (error) {
        reject(error);
      }
    });
  }

  public queryHeartbeat(): Promise<boolean>
  {
    return new Promise<boolean>((resolve, reject) => {
      if (!this._heartbeatURL) {
        return resolve(true);
      }

      this.http
        .head(this._heartbeatURL)
        .subscribe(_ => resolve(true), _ => resolve(false));
    });
  }

  public get heartbeatURL(): string
  {
    return this._heartbeatURL;
  }

  public set heartbeatURL(heartbeatURL: string)
  {
    this._heartbeatURL = heartbeatURL;
  }

  public get designResultsURL(): string
  {
    return this._designResultsURL;
  }

  public set designResultsURL(designResultsURL: string)
  {
    this._designResultsURL = designResultsURL;
  }

  private buildPostData(cartURL: string, productStoreURL: string,
      catalogStoreId: string, locale: string, productConfig: ProductConfig): any
  {
    let postData = {
        cart_url: cartURL,
        product_store_url: productStoreURL,
        catalog_store_id: catalogStoreId,
        locale: locale,
        products: [ ],
    };
    postData.products.push(this.getProductPostData(productConfig));
    for (let additionalProductId of productConfig.selectedAdditionalProducts) {
      for (let additionalProductConfig of productConfig.additionalProducts) {
        if (additionalProductConfig.id === additionalProductId) {
          postData.products.push(this.getProductPostData(
              additionalProductConfig));
          break;
        }
      }
    }
    return postData;
  }

  /**
   * @throws BuildPostDataError
   */
  private getProductPostData(productConfig: ProductConfig): any
  {
    let productPostData = {
        catalog_product_id: productConfig.id,
        quantity: productConfig.quantity,
        parts: this.getProductParts(productConfig.collageDesignerConfig),
        feature_selections: this.getProductFeatureSelections(productConfig),
    };
    if (productConfig.productStoreURL) {
      productPostData['product_store_url'] = productConfig.productStoreURL;
    }

    return productPostData;
  }

  /**
   * @throws BuildPostDataError
   */
  private getProductParts(collageDesignerConfig: any): any
  {
    let parts = {};
    if (collageDesignerConfig) {
      for (let stepId of Object.keys(collageDesignerConfig.steps)) {
        for (let part of collageDesignerConfig.steps[stepId].parts) {
          const structure_info = this.getStructureInfo(
            collageDesignerConfig.steps[stepId], parseInt(stepId, 10), part);
          if (structure_info) {
            parts[part.id] = structure_info;
          }
        }
      }
    }

    return parts;
  }

  /**
   * @throws BuildPostDataError
   */
  private getStructureInfo(
    stepConfig: any, stepId: number, part: any): StructureInfo | undefined
  {
    const editingViewType = part.editingView.type;

    const lookupStructure =
      (selectedPart: any, selectedNodeTag?: string): StructureInfo => {
        const designState =
          this.designStepService.getDesignState(stepId, selectedPart.id);

        if (!designState) {
          return undefined;
        }

        const rawStructure = Utils
          .find(selectedPart.templateGroups,
                g => g.id === designState.templateGroupId)
          .templates[designState.templateId]
          .stageConfig
          .svgStructure;

        return {
          // IE11-Hack: Der IE wirft den pap-namespace aus Unter-SVGs beim POST
          structure: StageUtils.ensureHasPapNamespace(
            StageUtils.sanitizeStructure(
              rawStructure, true, true, selectedNodeTag)),
          template_group: designState.templateGroupId,
          template: designState.templateId,
        };
      };

    switch (editingViewType) {
      case 'stage': // fall through to 'default'
      case 'default': return lookupStructure(part);

      case 'reference': {
        const stagePart =
          stepConfig.parts.filter(p => p.editingView.type === 'stage')[0];

        return lookupStructure(
          stagePart, part.editingView.reference || part.id);
      }

      default: {
        const message = 'invalid `editing_view.type`: ' + editingViewType;
        this.loggingService.log(
            'ProductdesignerdService', Severity.ERROR, message);
        throw new BuildPostDataError(message);
      }
    }
  }

  private getProductFeatureSelections(productConfig: ProductConfig): any
  {
    let features = {};
    for (let featureGroupId of Object.keys(productConfig.featureGroups)) {
      features[featureGroupId] =
          productConfig.featureGroups[featureGroupId].selectedFeature;
    }
    return features;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/productdesignerd/productdesignerd.service.ts